"use client";

import { type FC, type ReactNode, useEffect, useRef, useState } from "react";
import {
  Card,
  Flex,
  ValueParser,
  Gauge,
  Text,
  Spacing,
  type SlidesRef,
  Slides,
  ButtonToggle,
  type ButtonToggleValue,
} from "@huub-web/ui";
import { useTranslation } from "react-i18next";
import { limit } from "@huub-web/utils";

import { InfoIcon } from "../../InfoIcon";
import type { MaximumMortgageCalculationResult } from "../../../types";

import {
  bannerCardContentStyle,
  bannerCardInnerStyle,
  cardContentStyle,
  cardInnerStyle,
  cardStyle,
  gaugeStyle,
  slideContainerStyle,
  totalCostStyle,
  trackStrokeStyle,
} from "./CostCalculation.css";

export type CostCalculationProps = {
  cta: ReactNode;
  fundablePercentage: MaximumMortgageCalculationResult["fundablePercentage"];
  rows: MaximumMortgageCalculationResult["costCalculation"];
  resultType: MaximumMortgageCalculationResult["resultType"];
  costCalculationDescription?: string;
  street?: string;
  hasPartner?: boolean;
  valuesListing: ReactNode;
  isRenovation?: boolean;
  isRefinance?: boolean;
};

export const CostCalculation: FC<CostCalculationProps> = ({
  cta,
  fundablePercentage,
  resultType,
  costCalculationDescription,
  street,
  rows = [],
  hasPartner = false,
  valuesListing,
  isRenovation = false,
  isRefinance = false,
}) => {
  const { t } = useTranslation();

  const cardRef = useRef<HTMLDivElement>(null);
  const slidesRef = useRef<SlidesRef>(null);

  const parsedFundablePercentage = limit(fundablePercentage, 0, 100);

  const [activeSlide, setActiveSlide] = useState<
    "costCalculation" | "dataOverview"
  >("costCalculation");

  const setSlide = (slide: ButtonToggleValue) => {
    setActiveSlide(slide as "costCalculation" | "dataOverview");
    if (cardRef.current) {
      const elementTop = cardRef.current.getBoundingClientRect().top;
      const offsetPosition = window.scrollY + elementTop - 80;
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    slidesRef.current?.goTo(activeSlide);
  }, [activeSlide]);

  return (
    <Card
      ref={cardRef}
      className={cardStyle}
      innerClassName={cardInnerStyle}
      contentClassName={cardContentStyle}
      variant="tertiary"
      border="backgroundThick"
      applyPadding={false}
      elevated
    >
      <Slides
        ref={slidesRef}
        slides={{
          costCalculation: (
            <Flex className={slideContainerStyle} direction="column">
              <Flex direction="column" gap="s">
                <Flex direction="column" gap="xxs">
                  <Text as="h4" variant="displayM" bold>
                    {t("maxMortgageWizard.result.costCalculation.title")}
                  </Text>
                  {street && (
                    <Text variant="bodyL" bold>
                      {t("maxMortgageWizard.result.costCalculation.forStreet", {
                        street,
                      })}
                    </Text>
                  )}
                </Flex>
                {costCalculationDescription && (
                  <Text as="p">{costCalculationDescription}</Text>
                )}
                <Spacing />
              </Flex>

              <Flex as="ul" gap="xxs" direction="column">
                {rows?.map((row, rowIndex) => (
                  <Flex key={rowIndex} direction="column" gap="xxs">
                    {row?.values.map((value, valueIndex) => (
                      <Flex
                        key={`value-${rowIndex}-${valueIndex}`}
                        as="li"
                        gap="s"
                        align="flex-start"
                        justify="space-between"
                      >
                        <Text color="currentColor75">
                          {value.title}{" "}
                          {value.help && <InfoIcon value={value.help} />}
                        </Text>
                        <Text>
                          <ValueParser
                            value={{ value: value.value, type: "money" }}
                          />
                        </Text>
                      </Flex>
                    ))}
                    {row?.totals.map((total, totalIndex) => (
                      <Flex
                        key={`total-${rowIndex}-${totalIndex}`}
                        as="li"
                        gap="s"
                        align="flex-start"
                        justify="space-between"
                        className={
                          row.totals && row.totals.length > 0
                            ? totalCostStyle
                            : undefined
                        }
                      >
                        <Text bold={totalIndex === 0}>
                          {total.title}{" "}
                          {total.help && <InfoIcon value={total.help} />}
                        </Text>
                        <Text bold={totalIndex === 0}>
                          <ValueParser
                            value={{ value: total.value, type: "money" }}
                          />
                        </Text>
                      </Flex>
                    ))}
                    <Spacing height="s" />
                  </Flex>
                ))}
              </Flex>

              {!isRenovation && (
                <Card
                  innerClassName={bannerCardInnerStyle}
                  contentClassName={bannerCardContentStyle}
                >
                  <Gauge
                    value={parsedFundablePercentage}
                    max={100}
                    color="gradient"
                    size="m"
                    className={gaugeStyle}
                    trackClassName={trackStrokeStyle}
                    needle={false}
                    showValue
                  />
                  <Flex direction="column" gap="xxs" grow>
                    {(() => {
                      const subject = isRefinance ? "Refinance" : "House";
                      const thisSubjectKey = `maxMortgageWizard.result.subject.this${subject}`;
                      const theSubjectKey = `maxMortgageWizard.result.subject.the${subject}`;

                      return (
                        <>
                          <Text bold>
                            {t(
                              `maxMortgageWizard.result.${resultType}.costCalculation.gapTitle.${hasPartner ? "plural" : "singular"}`,
                              {
                                thisSubject: t(thisSubjectKey),
                                theSubject: t(theSubjectKey),
                              },
                            )}
                          </Text>
                          <Text as="p">
                            {t(
                              `maxMortgageWizard.result.${resultType}.costCalculation.gapDescription.${hasPartner ? "plural" : "singular"}`,
                              {
                                thisSubject: t(thisSubjectKey),
                                theSubject: t(theSubjectKey),
                              },
                            )}
                          </Text>
                        </>
                      );
                    })()}
                  </Flex>

                  {cta}
                </Card>
              )}
            </Flex>
          ),
          dataOverview: (
            <Flex className={slideContainerStyle} direction="column">
              {valuesListing}
            </Flex>
          ),
        }}
        initialSlide="costCalculation"
      />
      <ButtonToggle
        alignSelf="center"
        options={[
          {
            value: "costCalculation",
            label: t("maxMortgageWizard.result.costCalculation.tabLabel"),
          },
          {
            value: "dataOverview",
            label: t(
              `maxMortgageWizard.result.valuesListing.yourData.${hasPartner ? "plural" : "singular"}`,
            ),
          },
        ]}
        value={activeSlide}
        onChange={setSlide}
      />
    </Card>
  );
};
